// pages/Oati/OattInvoicePage.tsx
// Reads from oatt_invoice_computed + oatt_invoice_data (persisted after "Create Billing Form").
// Page title: "<customer_code> — [Revised] <Month YYYY> OATT Invoice"

import { useState, useEffect } from 'react';
import { useOattSession } from '@hooks/useOattSession';
import { Empty, Input, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMutation, useQuery } from '@apollo/client';
import { GET_OATT_INVOICE, LOG_OATT_INVOICE_DOWNLOAD, SAVE_OATT_MISC_CHARGE, APPROVE_OATT_INVOICE } from '@graphql/oattinvoice.gql';
import { NSP_CORE_INFO_BY_TYPE } from '@graphql/invoices.gql';
import PageLayout from '@components/layout/PageLayout';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import { NSPButton } from '@components/ui/NSPButton';
import { generateOattInvoicePDF } from '@utils/oatt-invoice-generator';
import useAuthRoles from '@hooks/useAuthRoles';
import styles from './OattPages.module.css';
import { useOattCustomer } from '@hooks/useOattcustomer';

dayjs.extend(utc);
// ── Types (mirror GQL schema) ─────────────────────────────────────────────────

interface OattInvoiceLineItem {
  invoice_data_id: number;
  aref: string;
  seller: string;
  customer_code: string;
  mw_granted: number;
  total_charges_oatt: number;
  total_charges_computed: number;
  firm_transmission_charge: number;
  nonfirm_transmission_charge: number;
  ancillary_schedule1: number;
  ancillary_schedule2: number;
  total_charges: number;
}

interface OattInvoice {
  invoice_id: number;
  customer_id: number;
  customer_code: string;
  account_number: string | null;
  billing_period: string | null;
  line_items: OattInvoiceLineItem[];
  total_firm_transmission_charge: number;
  total_nonfirm_transmission_charge: number;
  total_ancillary_schedule1: number;
  total_ancillary_schedule2: number;
  oatt_charges_total: number;
  hst: number;
  hst_amount: number;
  total_bill: number;
  is_revision: boolean | null;
  revision_no: number | null;
  supersedes_invoice_id: number | null;
  company_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_line3: string | null;
  misc_charge: number | null;
  misc_comment: string | null;
  is_approved: boolean | null;
  revision_comment: string | null;
  vendor_comment: string | null;
}

// ── Formatters ────────────────────────────────────────────────────────────────

const fmtMoney = (v: number | null | undefined): string => {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtNum = (v: number | null | undefined): string =>
  v == null ? '—' : Number(v).toLocaleString('en-US');

// ── Line-item columns ─────────────────────────────────────────────────────────

const reservationColumns: ColumnsType<OattInvoiceLineItem & { key: number }> = [
  { title: 'Customer', dataIndex: 'customer_code', key: 'customer_code', width: 140 },
  { title: 'ARef', dataIndex: 'aref', key: 'aref', width: 130 },
  { title: 'Seller', dataIndex: 'seller', key: 'seller', width: 100 },
  { title: 'MW Granted', dataIndex: 'mw_granted', key: 'mw_granted', width: 110, align: 'right', render: fmtNum },
  {
    title: 'OATT Charges', dataIndex: 'total_charges', key: 'total_charges',
    width: 150, align: 'right',
    render: (v: number) => <span>{fmtMoney(v)}</span>,
  },
  {
    title: 'Final Charges', dataIndex: 'total_charges', key: 'final_charges',
    width: 150, align: 'right',
    render: (v: number) => <strong>{fmtMoney(v)}</strong>,
  },
];

// ── Customer Invoice Card ─────────────────────────────────────────────────────

interface CustomerCardProps {
  invoice: OattInvoice;
  monthLabel: string;
  primaryIssuer: any;
  downloading: boolean;
  onDownload: () => void;
  onRefetch: () => void;
  canPrepare: boolean;
  canApprove: boolean;
}

const CustomerCard = ({
  invoice,
  monthLabel: _monthLabel,
  primaryIssuer: _primaryIssuer,
  downloading,
  onDownload,
  onRefetch,
  canPrepare,
  canApprove,
}: CustomerCardProps) => {
  const [expanded, setExpanded] = useState(true);
  const [miscDescription, setMiscDescription] = useState(invoice.misc_comment ?? '');
  const [miscAmount, setMiscAmount] = useState(invoice.misc_charge != null ? String(invoice.misc_charge) : '');
  const [isSaved, setIsSaved] = useState(invoice.misc_charge != null);
  const [isApproved, setIsApproved] = useState(Boolean(invoice.is_approved));
  const [saveMiscCharge, { loading: saving }] = useMutation(SAVE_OATT_MISC_CHARGE);
  const [approveInvoice, { loading: approving }] = useMutation(APPROVE_OATT_INVOICE);

  const hasMiscCharge = miscDescription.trim() !== '' || miscAmount.trim() !== '';
  const isDirty = hasMiscCharge && !isSaved;
  const miscAmountNum = parseFloat(miscAmount) || 0;

  // Before save: compute totals locally for live preview.
  // After save: BE returns updated totals (including misc) via refetch.
  const adjustedOattTotal = invoice.oatt_charges_total + (isSaved ? 0 : miscAmountNum);
  const adjustedHstAmount = isSaved ? invoice.hst_amount : adjustedOattTotal * (invoice.hst / 100);
  const adjustedGrandTotal = isSaved ? invoice.total_bill : adjustedOattTotal + adjustedHstAmount;

  // Warn on browser refresh / tab close when there are unsaved misc changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleMiscDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMiscDescription(e.target.value);
    if (isSaved) setIsSaved(false);
  };

  const handleMiscAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and a single decimal point
    const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setMiscAmount(val);
    if (isSaved) setIsSaved(false);
  };

  const handleSave = async () => {
    const result = await saveMiscCharge({
      variables: {
        invoiceId: invoice.invoice_id,
        miscCharge: miscAmountNum || null,
        miscComment: miscDescription.trim() || null,
      },
    });
    if (result.data?.saveOattMiscCharge?.success) {
      setIsSaved(true);
      onRefetch();
    }
  };

  const handleApprove = async () => {
    const result = await approveInvoice({ variables: { invoiceId: invoice.invoice_id } });
    if (result.data?.approveOattInvoice?.success) {
      setIsApproved(true);
      onRefetch();
    }
  };

  // Truly disabled (grey) when not a preparer or already approved.
  // Read-only (dark, non-editable) when saved but can still be unlocked via Edit.
  const isFieldDisabled = !canPrepare || isApproved;
  const isFieldReadOnly = isSaved && canPrepare && !isApproved;

  // Action button logic
  let actionButton: React.ReactNode;
  if (!isSaved) {
    // Not yet saved — show Download Invoice (disabled while there are unsaved misc changes)
    actionButton = (
      <NSPButton onClick={onDownload} loading={downloading} disabled={isDirty}>
        Download Invoice
      </NSPButton>
    );
  } else if (!isApproved) {
    // Saved but not approved — approver sees active button, preparer sees greyed out
    actionButton = (
      <NSPButton onClick={canApprove ? handleApprove : undefined} disabled={!canApprove} loading={approving}>
        Approve Invoice
      </NSPButton>
    );
  } else {
    // Approved — everyone can download
    actionButton = (
      <NSPButton onClick={onDownload} loading={downloading}>
        Download Invoice
      </NSPButton>
    );
  }

  return (
    <div className={styles.customerCard}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <button
            className={styles.expandBtn}
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </button>
          <div>
            <div className={styles.customerName}>
              {invoice.customer_code}
              {invoice.is_revision && (
                <Tag color="orange" style={{ marginLeft: 10, fontSize: 11 }}>
                  Revision {invoice.revision_no}
                </Tag>
              )}
            </div>
            <div className={styles.customerMeta}>
              Account Number: {invoice.account_number ?? invoice.customer_id}
              <span style={{ marginLeft: 20 }} />
              Service for Month: {dayjs(invoice.billing_period).format('MMMM YYYY')}
            </div>
          </div>
        </div>

        <div className={styles.cardHeaderCenter}>
          <div className={styles.cardActionGroup}>
            {isSaved && !isApproved && canPrepare ? (
              <NSPButton onClick={() => setIsSaved(false)}>
                Edit
              </NSPButton>
            ) : (
              <NSPButton disabled={!hasMiscCharge || isSaved || isApproved} loading={saving} onClick={handleSave}>
                Save Changes
              </NSPButton>
            )}
            {actionButton}
          </div>
        </div>

        <div className={styles.cardHeaderRight}>
          <div className={styles.chargeSummaryItem}>
            <span className={styles.chargeSummaryLabel}>Total Charge (Excl. Tax)</span>
            <span className={styles.chargeSummaryValue}>{fmtMoney(adjustedOattTotal)}</span>
          </div>
          <div className={styles.chargeSummaryItem}>
            <span className={styles.chargeSummaryLabel}>Total Charge (Incl. Tax)</span>
            <span className={`${styles.chargeSummaryValue} ${styles.grandValue}`}>
              {fmtMoney(adjustedGrandTotal)}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div className={styles.cardBody}>
          <div className={styles.summaryBox}>
            <div className={styles.summarySection}>
              <div className={styles.summarySectionTitle}>Transmission Service</div>
              <div className={styles.summaryRow}>
                <span>Schedule 7 — PTP Transmission Service / Firm</span>
                <span className={styles.summaryAmount}>{fmtMoney(invoice.total_firm_transmission_charge)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Schedule 8 — PTP Transmission Service / Non-Firm</span>
                <span className={styles.summaryAmount}>{fmtMoney(invoice.total_nonfirm_transmission_charge)}</span>
              </div>
            </div>

            <div className={styles.summarySection}>
              <div className={styles.summarySectionTitle}>Ancillary Services</div>
              <div className={styles.summaryRow}>
                <span>Schedule 1 — Scheduling, System Control, &amp; Dispatch</span>
                <span className={styles.summaryAmount}>{fmtMoney(invoice.total_ancillary_schedule1)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Schedule 2 — Reactive Control and Voltage Control</span>
                <span className={styles.summaryAmount}>{fmtMoney(invoice.total_ancillary_schedule2)}</span>
              </div>
            </div>

            {/* Miscellaneous Charge */}
            <div className={styles.summarySection}>
              <div className={styles.summaryRow}>
                <span className={styles.summarySectionTitle}>Miscellaneous Charge</span>
                <div className={styles.miscAmountWrapper}>
                  <span className={styles.dollarPrefix}>$</span>
                  <Input
                    className={styles.miscAmountInput}
                    placeholder="0.00"
                    value={miscAmount}
                    onChange={handleMiscAmountChange}
                    disabled={isFieldDisabled}
                    readOnly={isFieldReadOnly}
                  />
                </div>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.miscCommentLabel}>Comment</span>
                <Input.TextArea
                  className={styles.miscDescInput}
                  placeholder="Add description of charge here"
                  value={miscDescription}
                  onChange={handleMiscDescriptionChange}
                  disabled={isFieldDisabled}
                  readOnly={isFieldReadOnly}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                />
                <div className={styles.commentRowSpacer} />
              </div>
            </div>

            <hr className={styles.summaryDivider} />

            <div className={styles.summaryRow}>
              <span className={styles.subtotalLabel}>OATT Charges Total</span>
              <span className={styles.summaryAmount}>{fmtMoney(adjustedOattTotal)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>HST: {invoice.hst}%</span>
              <span className={styles.summaryAmount}>{fmtMoney(adjustedHstAmount)}</span>
            </div>

            <hr className={styles.summaryDivider} />

            <div className={styles.grandTotalRow}>
              <span>Grand Total of Invoice</span>
              <span className={styles.grandTotalAmount}>{fmtMoney(adjustedGrandTotal)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const OattInvoicePage = () => {
  const { selectedDate, setSelectedDate } = useOattSession();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const { customerId, customerCode, CustomerSelect } = useOattCustomer();
  const { isOattBillPreparer, isOattBillApprover } = useAuthRoles();

  const selectedMonth = selectedDate.month() + 1;
  const selectedYear = selectedDate.year();
  const monthLabel = selectedDate.format('MMMM YYYY');

  const { data, loading, error, refetch } = useQuery<{ getOattInvoice: OattInvoice[] }>(
    GET_OATT_INVOICE,
    { variables: { month: selectedMonth, year: selectedYear, customerId }, fetchPolicy: 'no-cache' }
  );

  const [logDownload] = useMutation(LOG_OATT_INVOICE_DOWNLOAD);
  // NSP core info for PDF (logo, address, etc.)
  const { data: coreInfoData } = useQuery(NSP_CORE_INFO_BY_TYPE, {
    variables: { entryType: 'primary' },
    fetchPolicy: 'cache-first',
  });
  const primaryIssuer = coreInfoData?.nspCoreInfoByType ?? null;

  const invoices = data?.getOattInvoice ?? [];
  const isRevisionData = invoices.length > 0 && Boolean(invoices[0].is_revision);
  const customerLabel = customerCode ?? 'Select Customer';
  // ── Page title: "<customer_code> — [Revised] <Month YYYY> OATT Invoice"
  const pageTitle = isRevisionData
    ? `${customerLabel} — Revised ${monthLabel} OATT Invoice`
    : `${customerLabel} — ${monthLabel} OATT Invoice`;

  const disabledDate = (current: Dayjs) => current && current >= dayjs().startOf('month');

  const handleDownload = async (invoice: OattInvoice) => {
    setDownloadingId(invoice.invoice_id);
    try {
      await generateOattInvoicePDF({ invoice, monthLabel, primaryIssuer, accountNumber: invoice.account_number ?? undefined });
      await Promise.all([
        logDownload({ variables: { invoiceId: invoice.invoice_id, month: selectedMonth, year: selectedYear, customerId } }),
      ]);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <PageLayout
      title={pageTitle}
      headerAccessoryView={
        <div
          className={styles.periodSelector}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <div className={styles.customerSelectWrapper}>
            <CustomerSelect />
          </div>

          <NSPDatePicker
            picker="month"
            format="MMM YYYY"
            allowClear={false}
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            disabledDate={disabledDate}
          />
        </div>
      }
    >


      <div className={styles.content}>
        {loading && <div data-testid="loading-spinner" style={{ textAlign: 'center', padding: '60px 0' }}><Spin size="large" /></div>}

        {!loading && error && <Empty description={`Error loading invoices: ${error.message}`} />}

        {!loading && !error && invoices.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No invoice data for ${monthLabel}. Use "Create Billing Form" on the Validation page to generate invoices.`}
          />
        )}

        {!loading && !error && invoices.map((invoice) => (
          <CustomerCard
            key={invoice.invoice_id}
            invoice={invoice}
            monthLabel={monthLabel}
            primaryIssuer={primaryIssuer}
            downloading={downloadingId === invoice.invoice_id}
            onDownload={() => handleDownload(invoice)}
            onRefetch={refetch}
            canPrepare={isOattBillPreparer}
            canApprove={isOattBillApprover}
          />
        ))}

        {!loading && !error && invoices.length > 0 && (() => {
          const allLineItems = invoices.flatMap((inv) =>
            inv.line_items.map((item) => ({ ...item, key: item.invoice_data_id }))
          );
          return (
            <div className={styles.reservationSection}>
              <div className={styles.customerCard}>
                <div className={styles.cardHeader}>
                  <span className={styles.reservationSeparatorLabel}>Reservation Details
                    <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
                      {allLineItems.length} line item{allLineItems.length !== 1 ? 's' : ''}
                    </Tag></span>
                </div>

                <div className={styles.cardBody}>
                  <Table
                    dataSource={allLineItems}
                    columns={reservationColumns}
                    pagination={false}
                    size="small"
                    className={styles.lineTable}
                    tableLayout="fixed"
                    virtual
                    scroll={{ y: 500 }}
                  />
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </PageLayout>
  );
};

export default OattInvoicePage;
